import { CommonModule, NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController } from '@ionic/angular';
import _ from 'lodash';
import { v4 as uuid } from 'uuid';

import { INTEGRATION_TYPES } from 'src/app/constants';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { NangoService } from 'src/services/nango.service';

@Component({
  standalone: true,
  selector: 'app-edit-integration',
  templateUrl: './integration-input-modal.html',
  styleUrls: ['./integration-input-modal.css'],
  imports: [CommonModule, IonicModule, FormsModule, NgClass],
})
export class IntegrationInputModalComponent implements OnInit {
  integration: any;
  integrationSettings: any;
  integrationOutput: any;

  isSavingIntegration = false;

  constructor(
    public authService: AuthService,
    private msgSrvc: MessageService,
    private modalCtrl: ModalController,
    private apiService: ApiService,
    private nangoService: NangoService,
  ) {}

  ngOnInit() {
    this.integrationSettings = this.integrationSettings ?? {};

    // no user input is required. go to registration
    if (!this.integration.userInput) {
      return this.registerIntegration();
    }

    if (!this.integrationSettings.userInput) {
      this.integrationSettings.userInput = {};
    }

    if (!this.integrationSettings.nangoConfig) {
      this.integrationSettings.nangoConfig = {};
    }

    this.integrationSettings.userInput = {
      ...this.integrationSettings.userInput,
      ...this.integrationSettings.nangoConfig,
    };

    this.integrationSettings.userInput = {
      ...this.integration.userInput?.reduce((acc, inputField) => {
        if (inputField['default'] !== undefined) {
          acc[inputField.key] = inputField['default'];
        }

        return acc;
      }, {}),
      ...this.integrationSettings.userInput,
    };
  }

  onDismissIntegrationInputModal(data: any = null) {
    this.modalCtrl.dismiss(data);
    (this as any).modal?.dismiss(data);
  }

  async onSubmitIntegrationInputModal() {
    await this.registerIntegration();
  }

  shouldShowHelper(inputField: any) {
    if (inputField.helper) {
      const value = this.integrationSettings.userInput[inputField.key];
      return !value || (inputField.format === 'snakeCase' && value.includes(' '));
    }

    return false;
  }

  async registerIntegration() {
    this.isSavingIntegration = true;

    try {
      const mergedInput = {
        ...(this.integrationSettings.userInput ?? {}),
        ...(this.integrationSettings.nangoConfig ?? {}),
      };
      let nangoConfig: any;
      let params: any;
      let secrets: any;

      if (!_.isEmpty(mergedInput)) {
        ({ nangoConfig, params, secrets } = Object.keys(mergedInput).reduce(
          (acc, key) => {
            const field = this.integration.userInput?.find((field) => field.key === key);

            if (field?.type === 'secret') {
              acc.secrets[key] = mergedInput[key];
            } else if (field?.isNangoConfig) {
              acc.nangoConfig[key] = mergedInput[key];
            } else {
              acc.params[key] = mergedInput[key];
            }

            return acc;
          },
          { nangoConfig: {}, params: {}, secrets: {} } as any,
        ));
      }

      const nangoIntegrationId = this.integrationSettings.nangoIntegrationId ?? this.integration.key;

      let connectionId: string;

      if (this.integration.isNango) {
        ({ connectionId } = await this.nangoService.authorize(
          nangoIntegrationId,
          nangoConfig,
          this.integration.multipleConnections ? [uuid()] : undefined,
          this.integration.authorizationParams,
        ));
      }

      const result: any = await this.apiService.postPromise(`/integrations/register`, {
        integration: nangoIntegrationId,
        integrationType: this.integration.type,
        isNango: this.integration.isNango,
        connectionId,
        params,
        secrets,
      });

      this.integrationOutput = result.data;

      await this.authService.loadCompany();

      if (this.integration.type === INTEGRATION_TYPES.KNOWLEDGEBASE) {
        this.msgSrvc.show('Connected! Syncing docs now');
      } else {
        this.msgSrvc.show('Success!');
      }
      this.onDismissIntegrationInputModal({ saved: true, integrationOutput: this.integrationOutput });
    } catch (err: any) {
      this.apiService.postPromise('/log-entries', {
        level: 'error',
        data: {
          message: err.message,
          stack: err.stack,
          code: err.code,
          apiError: err?.error,
        },
      });
      this.msgSrvc.showError(err);
    } finally {
      this.isSavingIntegration = false;
    }
  }
}
